import type { CanvasDataSource, CanvasType } from "../types";
import type { CanvasBuilderElement } from "./canvasBuilderProjection";
import { generateCanvasDataSourceAlias } from "./canvasDataSourceAlias";
import { normalizeHierarchyConfig } from "../components/canvasBuilder/properties/hierarchyBuilder";
import { canvasTypeToArchetype } from "./canvasArchetype";

export interface CanvasMutationPayloadInput {
  name: string;
  description: string;
  canvasType: CanvasType;
  isWizard: boolean;
  elements: CanvasBuilderElement[];
  dataSources: CanvasDataSource[];
}

export interface ChildCanvasUpdatePayloadInput {
  name: string;
  description: string;
  canvasType: CanvasType;
  isWizard: boolean;
  elements: CanvasBuilderElement[];
  dataSources: CanvasDataSource[];
}

export { generateCanvasDataSourceAlias } from "./canvasDataSourceAlias";

export const VIEW_GRID_REQUIRES_DATA_SOURCE =
  "Add a data source before adding a View Grid";

export function buildChildCanvasUpdatePayload({
  name,
  description,
  canvasType,
  isWizard,
  elements,
  dataSources,
}: ChildCanvasUpdatePayloadInput) {
  const content = buildCanvasContentPayload({ elements, dataSources });
  return {
    name,
    description,
    archetype: canvasTypeToArchetype(canvasType),
    is_wizard: isWizard,
    ...content,
  };
}

const mapElementTypeToBackend = (type: string): string => {
  const typeMap: Record<string, string> = {
    field: "FIELD",
    container: "CONTAINER",
    text: "STATIC",
    button: "FIELD",
    action_button: "ACTION_BUTTON",
    table: "WIDGET",
    chart: "WIDGET",
    widget: "WIDGET",
    timeline: "WIDGET",
    kanban: "WIDGET",
    calendar: "WIDGET",
    hierarchy: "WIDGET",

    image: "STATIC",
    divider: "STATIC",
    tabs: "CONTAINER",
    accordion: "CONTAINER",
    card: "CONTAINER",
    embedded_canvas: "EMBEDDED_CANVAS",
    view_grid: "VIEW_GRID",
    related_records: "WIDGET",
    row: "CONTAINER",
    column: "CONTAINER",
  };
  return typeMap[type] || type.toUpperCase();
};

function buildCanvasContentPayload({
  elements,
  dataSources,
}: Pick<CanvasMutationPayloadInput, "elements" | "dataSources">) {
  const canvasDataSources = dataSources.map((ds, idx) => ({
    view_id: ds.view_id,
    alias: generateCanvasDataSourceAlias(ds.name, idx),
    is_primary: ds.is_primary ?? idx === 0,
    default_filters: ds.default_filters,
    sort_config: ds.sort_config,
  }));

  const dsIdToAlias: Record<string, string> = {};
  dataSources.forEach((ds, idx) => {
    dsIdToAlias[ds.id] = generateCanvasDataSourceAlias(ds.name, idx);
  });

  const tempIdToId: Record<string, string> = {};
  elements.forEach((el) => {
    tempIdToId[el.tempId] = el.id || el.tempId;
  });

  const canvasElements = elements.map((el, idx) => {
    const props = {
      ...(el.props as unknown as Record<string, unknown>),
    };

    if (el.type === "hierarchy") {
      const graph = normalizeHierarchyConfig(
        props.hierarchy_config,
        dataSources,
      );
      props.widget_type = "HIERARCHY";
      props.hierarchy_config = graph;
      props.data_source_alias = graph.nodes[0].data_source_alias;
    }

    if (
      el.type === "view_grid" ||
      (el.type === "widget" && Boolean(props.data_source_alias)) ||
      el.type === "timeline" ||
      el.type === "kanban" ||
      el.type === "calendar" ||
      el.type === "related_records"
    ) {
      const requestedAlias =
        typeof props.data_source_alias === "string"
          ? props.data_source_alias
          : "";
      const sourceIndex = dataSources.findIndex((ds, dsIndex) => {
        const declaredAlias = generateCanvasDataSourceAlias(ds.name, dsIndex);
        return (
          ds.id === requestedAlias ||
          ds.name === requestedAlias ||
          declaredAlias === requestedAlias
        );
      });
      const source = dataSources[sourceIndex];
      if (!source?.view_id) {
        throw new Error(
          el.type === "timeline"
            ? "Add a data source before adding a Timeline"
            : el.type === "kanban"
              ? "Add a data source before adding a Kanban board"
              : el.type === "calendar"
                ? "Add a data source before adding a Calendar"
                : VIEW_GRID_REQUIRES_DATA_SOURCE,
        );
      }
      props.data_source_alias = generateCanvasDataSourceAlias(
        source.name,
        sourceIndex,
      );
      if (el.type === "related_records") {
        props.widget_type = "RELATED_RECORDS";
        const config = (props.related_records_config || {}) as Record<
          string,
          unknown
        >;
        const requestedTarget = String(config.related_data_source_alias || "");
        const targetIndex = dataSources.findIndex((candidate, index) =>
          [
            candidate.id,
            candidate.name,
            generateCanvasDataSourceAlias(candidate.name, index),
          ].includes(requestedTarget),
        );
        const target = dataSources[targetIndex];
        const fields = Array.isArray(config.display_fields)
          ? config.display_fields
          : [];
        const pageSize = config.max_records ?? 10;
        if (
          !target?.view_id ||
          typeof config.relationship_path !== "string" ||
          !config.relationship_path.trim() ||
          !fields.length ||
          fields.some((field) => typeof field !== "string" || !field.trim()) ||
          new Set(fields.map((field) => String(field).trim())).size !==
            fields.length ||
          typeof pageSize !== "number" ||
          !Number.isInteger(pageSize) ||
          pageSize < 1 ||
          pageSize > 100
        ) {
          throw new Error(
            "Configure both Views, a relationship, display fields and page size 1–100 for Related Records",
          );
        }
        props.related_records_config = {
          ...config,
          related_data_source_alias: generateCanvasDataSourceAlias(
            target.name,
            targetIndex,
          ),
          relationship_path: config.relationship_path.trim(),
          display_fields: fields.map((field) => String(field).trim()),
          max_records: pageSize,
        };
      }
      if (el.type === "timeline") {
        if (
          typeof props.label_field !== "string" ||
          !props.label_field.trim()
        ) {
          throw new Error("Select a stage label field for the Timeline");
        }
        props.widget_type = "TIMELINE";
        props.timeline_config = {
          label_field: props.label_field,
          status_field: props.status_field || null,
          date_field: props.date_field || null,
          description_field: props.description_field || null,
          order_field: props.order_field || null,
        };
        delete props.label_field;
        delete props.status_field;
        delete props.date_field;
        delete props.description_field;
        delete props.order_field;
      }

      if (el.type === "kanban") {
        const columns = Array.isArray(props.columns)
          ? (props.columns as unknown[])
              .map((column) => String(column).trim())
              .filter(Boolean)
          : [];
        if (
          typeof props.status_field !== "string" ||
          !props.status_field.trim()
        ) {
          throw new Error("Select a status field for the Kanban board");
        }
        if (columns.length === 0) {
          throw new Error("Add at least one column to the Kanban board");
        }
        if (new Set(columns).size !== columns.length) {
          throw new Error("Kanban board columns must be unique");
        }
        if (
          typeof props.title_field !== "string" ||
          !props.title_field.trim()
        ) {
          throw new Error("Select a card title field for the Kanban board");
        }
        props.widget_type = "KANBAN";
        props.kanban_config = {
          status_field: props.status_field,
          columns,
          title_field: props.title_field,
          subtitle_field: props.subtitle_field || null,
          ...(props.card_click_action
            ? { card_click_action: props.card_click_action }
            : {}),
        };
        delete props.status_field;
        delete props.columns;
        delete props.title_field;
        delete props.subtitle_field;
        delete props.card_click_action;
      }

      if (el.type === "calendar") {
        if (typeof props.date_field !== "string" || !props.date_field.trim()) {
          throw new Error("Select a date field for the Calendar");
        }
        if (
          typeof props.title_field !== "string" ||
          !props.title_field.trim()
        ) {
          throw new Error("Select an event title field for the Calendar");
        }
        if (props.end_date_field && props.end_date_field === props.date_field) {
          throw new Error("The Calendar end date must be a different field");
        }
        props.widget_type = "CALENDAR";
        props.calendar_config = {
          date_field: props.date_field,
          end_date_field: props.end_date_field || null,
          title_field: props.title_field,
          default_view: props.default_view === "week" ? "week" : "month",
          ...(props.event_click_action
            ? { event_click_action: props.event_click_action }
            : {}),
        };
        delete props.date_field;
        delete props.end_date_field;
        delete props.title_field;
        delete props.default_view;
        delete props.event_click_action;
      }

      if (el.type === "view_grid" && !Array.isArray(props.visible_columns)) {
        props.visible_columns = [];
      }
    }

    return {
      // Updates replace the element collection. Preserve stable IDs so nested
      // parent links, row provenance, and embedding references survive it.
      id: el.id || el.tempId,
      element_type: mapElementTypeToBackend(el.type),
      parent_element_id: el.parentElementId
        ? tempIdToId[el.parentElementId]
        : null,
      parent_panel_id: el.parentPanelId || null,
      grid_column: el.gridColumn,
      grid_row: el.gridRow,
      column_span: el.columnSpan,
      row_span: el.rowSpan,
      display_order: el.displayOrder ?? idx,
      name: el.name,
      is_visible: el.isVisible,
      visibility_rules: el.visibilityRules,
      config_json: {
        label: props.label,
        placeholder: props.placeholder,
        helperText: props.helperText,
        required: props.required,
        disabled: props.disabled,
        variant: props.variant,
        size: props.size,
        options: props.options,
        ...props,
        ...(el.type === "field"
          ? {
              auto_fill: el.autoFill ?? null,
              validation_rules: el.validationRules ?? [],
            }
          : {}),
      },
      data_binding:
        el.data_binding?.data_source_id && el.data_binding?.field_path
          ? {
              data_source_alias:
                dsIdToAlias[el.data_binding.data_source_id] ||
                el.data_binding.data_source_id,
              field_path: el.data_binding.field_path,
            }
          : null,
      style_overrides: el.styleOverrides,
      css_classes: el.cssClasses,
    };
  });

  return {
    elements: canvasElements,
    data_sources: canvasDataSources,
  };
}

/** Project the builder's working state into the exact create/update API shape. */
export function buildCanvasMutationPayload({
  name,
  description,
  canvasType,
  isWizard,
  elements,
  dataSources,
}: CanvasMutationPayloadInput) {
  return {
    name,
    description: description || undefined,
    archetype: canvasTypeToArchetype(canvasType),
    // Retain the legacy field for older API versions; current API uses archetype.
    canvas_type: canvasType,
    is_wizard: isWizard,
    ...buildCanvasContentPayload({ elements, dataSources }),
  };
}
